import Cookies from 'js-cookie';
import { get } from 'lodash-es';
import NProgress from 'nprogress';
import * as appConstants from '@/lib/constants/app';
import * as types from '@/lib/constants/store';
import i18next from '@/lib/i18next';
import { addLocalDataInterceptors } from '@/lib/localData';
import { cleanOrionTextModule } from '@/lib/utils';
import useErrorStore from '@/stores/error';

// get the env string
export function getEnv(hostname) {
  // use window hostname if one isn't provided
  const host = hostname || window.location.hostname;

  const regexResults = /(.*?)(.local|.stage|.qa|.dev|])\.discoveryeducation.(com|co\.uk|ca)/gi.exec(host);

  // If we've found a prefix, use that as the env
  if (regexResults && regexResults[2]) {
    return regexResults[2].replace(/[^a-z]/gi, '').toUpperCase();
  }

  return 'PROD';
}

export function getRegion(hostname) {
  // use window hostname if one isn't provided
  const host = hostname || window.location.hostname;

  const regexResults = /(.*?)\.discoveryeducation.(com|co\.uk|ca)/gi.exec(host);

  // If we've found a prefix, use that as the env
  if (regexResults && regexResults[2]) {
    const tld = regexResults[2];

    if (tld === 'com') {
      return 'US';
    }

    if (tld === 'co.uk') {
      return 'UK';
    }

    if (tld === 'ca') {
      return 'CA';
    }
  }

  // default
  return 'US';
}

export function getDomains(hostname) {
  // use window hostname if one isn't provided
  const host = hostname || window.location.hostname;

  const domains = {};
  const env = getEnv(host);
  const region = getRegion(host);

  // Grab the domain env vars (variables prefixed with `VUE_APP_` are available at runtime)
  domains.titanDomain = appConstants[`VUE_APP_${env}_API_${region}`];
  domains.deDomain = appConstants[`VUE_APP_${env}_DEAPI_${region}`];
  domains.appDomain = appConstants[`VUE_APP_${env}_APPURL_${region}`];
  domains.studioDomain = appConstants[`VUE_APP_${env}_STUDIO_URL_${region}`];
  domains.adminDomain = appConstants[`VUE_APP_${env}_ADMIN_URL_${region}`];
  domains.wwwDiscoveryEducationUrl = appConstants[`VUE_APP_WWW_DISCOVERY_EDUCATION_URL_${region}`];
  domains.staticUrl = appConstants[`VUE_APP_${env}_STATICURL_${region}`];
  domains.contentDomain = appConstants[`VUE_APP_${env}_CONTENT_API`];
  domains.atlasDomain = appConstants[`VUE_APP_${env}_ATLAS_URL`];

  if (region === 'US') {
    // Apollo only has dev and prod
    domains.apolloDomain = env === 'PROD'
      ? 'https://apollo.discoveryeducation.com'
      : 'https://apollo.dev.discoveryeducation.com';
  } else if (region === 'UK') {
    // Apollo only has dev and prod
    domains.apolloDomain = env === 'PROD'
      ? 'https://apollo.discoveryeducation.co.uk'
      : 'https://apollo.dev.discoveryeducation.co.uk';
  } else if (region === 'CA') {
    // Apollo only has dev and prod
    domains.apolloDomain = env === 'PROD'
      ? 'https://apollo.discoveryeducation.ca'
      : 'https://apollo.dev.discoveryeducation.ca';
  }

  return domains;
}

// get the domain to link back to the learn app
export function getAppDomain(hostname) {
  return getDomains(hostname).appDomain;
}

// get the atlas CMS domain
export function getAtlasDomain(hostname) {
  return getDomains(hostname).atlasDomain;
}

// get the EDDE API domain (for TEI questions) - uses DE API
export function getEddeApiDomain(hostname) {
  return getDomains(hostname).deDomain;
}

/**
 * Returns the domain to use for Google SSO links.
 *
 * @param {string} [hostname] - the hostname of the current page (uses window.location.hostname if not provided)
 * @return {string} the domain to use for Google SSO links
 */
export function getGoogleDomain(hostname) {
  return getDomains(hostname).appDomain.replace(/^https:\/\/app\./, 'https://google.');
}

// helper func to make it easier to spy on location changes
export function redirectToUrl(url) {
  window.location.href = url;
}

/**
 * Attach a response interceptor to catch 401's and reroute to the signin page
 * @param {Object} api Which api to attach the interceptor to
 * @param {Object} deApi DE api to use to get signin suggestions
 * @param {function} redirectCallback func to call to initiate a redirect
 */
export function attachErrorResponseInterceptor(api, deApiFetch, redirectCallback = null) {
  // make sure we have a redirect function
  const redirect = redirectCallback || redirectToUrl;

  // use a purposefully vague cookie name
  const errorCookieName = 'status_track';
  const limit = 5;

  // get domains
  const { appDomain } = getDomains(window.location.hostname);
  const cookieDomain = window.location.host;

  function handleErrorResponse(error) {
    // eslint-disable-next-line
    // grab the status code and message
    const statusCode = error.response?.status;
    // eslint-disable-next-line
    let message = error.response?.data;

    // deal with array buffers to translate the message if needed
    // this is because the shared_doc requests an arraybuffer response type
    if (statusCode === 403 && message instanceof ArrayBuffer && window.TextDecoder) {
      const decoder = new TextDecoder('utf-8');

      // decode the array buffer so that we can parse the string
      const decodedString = decoder.decode(message);

      try {
        // parse the json string and get the message out of it
        message = JSON.parse(decodedString);
        message = message.meta?.message || '';
      } catch {
        message = ''; // if the format wasn't as expected, then just use an empty string
      }
    } else if (message instanceof Object) {
      // get the message out of the response json object
      message = message.meta?.message || '';
    }

    // create a promise that we will use later
    const prom = new Promise((resolve, reject) => {
      if (
        (
          (
            statusCode === 401 // 401
            && !error.config?.url.includes('assessments') // assessments api uses a 401 response for more than login status
          )
          || (
            statusCode === 403 // OR 403 token expiration
            && message.includes('Token is too old')
          )
        ) && !error.config?.skipErrorHandling
      ) {
        // grab the cookie value
        const cookieVal = parseInt(Cookies.get(errorCookieName), 10) || 0;

        // if we met the limit, go back to the homepage
        if (cookieVal && cookieVal > limit) {
          Cookies.remove(errorCookieName, { domain: cookieDomain });

          // redirect to the learn homepage
          redirect(`${appDomain}/signin`);

          // reject the promise to clean everything up
          reject(error);
        } else {
          // increment
          Cookies.set(errorCookieName, cookieVal + 1, { expires: 7, domain: cookieDomain });

          // Match the interceptors used throughout the rest of the app
          const returnUrl = encodeURIComponent(window.location.href);
          deApiFetch(`/users/suggest_signin?next_url=${returnUrl}`)
            .then((signinResponse) => {
              if (!get(signinResponse, 'data.suggest.recommended')) {
                redirect(`${appDomain}/signin?next=${returnUrl}`);
              } else {
                redirect(signinResponse.data.suggest.recommended);
              }

              reject(error);
            }).catch(() => {
              redirect(`${appDomain}/signin?next=${returnUrl}`);
              reject(error);
            });
        }
      } else {
        reject(error);
      }
    });
    return prom;
  }

  return (...args) => (
    api(...args)
      .then((res) => {
        // Remove the cookie when we get a 200
        Cookies.remove(errorCookieName);
        return res;
      })
      .catch(handleErrorResponse)
  );
}

/**
 * This is a wrapper for the fetch api to speed up basic usage:
 *   - Expects a base url since it's used for Content, Edde, and Titan apis
 *   - Passes cookie and full referrer
 *   - Converts request body to json (can be overridden)
 *   - Converts response body to json
 *   - Throws if the api response with an error code
 *
 * To send a non-json request, pass `false` as the fourth argument and
 * set your own content-type header
 */
async function apiFetchClient(basePath, path, config = {}, convertRequestBodyToJson = true) {
  const headers = {};
  if (convertRequestBodyToJson) {
    headers['content-type'] = 'application/json';
  }

  const response = await fetch(
    `${basePath}${path}`,
    {
      credentials: 'include', // pass cookie
      referrerPolicy: 'no-referrer-when-downgrade', // pass full path for referrer
      ...config,
      body: convertRequestBodyToJson ? JSON.stringify(config.body) : config.body,
      headers: {
        ...headers,
        ...(config.headers || {}),
      },
    },
  );

  if (config.responseType === 'arraybuffer') {
    response.data = await response.arrayBuffer();
  } else {
    // Whether the request was successful or not, try to parse the response body if possible.
    // Naming the property 'data' mirrors the axios api.
    try {
      response.data = await response.json();
    } catch {
      // Allow non-json responses to pass as they are
    }
  }

  // Throw unsuccessful requests, and attach the response as a property just like axios does
  if (!response.ok) {
    const error = new Error(`Error while fetching: ${response.url}`);
    error.response = response;
    error.config = config; // Attach config so error interceptors can access it
    throw error;
  }

  // Return successful requests
  return response;
}

function interceptNetworkErrors(error) {
  const errorStore = useErrorStore();

  if (error.message?.match(/Network Error|Failed to fetch/)) {
    errorStore[types.SET_ERROR]({
      active: true,
      error: {
        meta: {
          status_code: i18next.t('Network Error'),
          message: i18next.t("Try refreshing the page. If that doesn't work, please contact the support team."),
        },
      },
    });
  } else if (error.message?.match(/timeout/i)) {
    errorStore[types.SET_ERROR]({
      active: true,
      error: {
        meta: {
          status_code: i18next.t('Timeout'),
          message: i18next.t("Try refreshing the page. If that doesn't work, please contact the support team."),
        },
      },
    });
  }
}

/**
 * Passes all args to the apiFetchClient, but with the Titan API base url.
 * The leading `/studio/v1` is included in the base path and should not be passed.
 * Also handles orion module cleaning for PATCH requests to drafts.
 */
function createTitanApiFetchClient(titanDomain, isLibrary = false) {
  return async (path, config = {}, convertBodyToJson = true) => {
    let finalConfig = config;

    // Handle orion module cleaning for PATCH requests to drafts
    if (config.method && config.method.toUpperCase() === 'PATCH' && path.includes('draft') && config.body) {
      // Clone the body to avoid mutating the original
      const cleanedBody = [...config.body];

      for (let x = 0; x < cleanedBody.length; x += 1) {
        if (
          cleanedBody[x].path && cleanedBody[x].path.includes('/pages')
          && typeof cleanedBody[x].value === 'object'
          && Array.isArray(cleanedBody[x].value.modules)
        ) {
          // clean up the modules
          cleanedBody[x].value.modules.forEach((mod) => cleanOrionTextModule(mod));
        }
      }

      // Update config with cleaned body
      finalConfig = { ...config, body: cleanedBody };
    }

    try {
      return await apiFetchClient(titanDomain, path, finalConfig, convertBodyToJson);
    } catch (error) {
      // Handle network errors for non-library mode
      if (!isLibrary) {
        interceptNetworkErrors(error);
      }
      throw error; // Re-throw the error after handling
    }
  };
}

function createApiFetchClient(baseUrl, isLibrary = false) {
  return async (path, config = {}, convertBodyToJson = true) => {
    try {
      return await apiFetchClient(baseUrl, path, config, convertBodyToJson);
    } catch (error) {
      // Handle network errors for non-library mode
      if (!isLibrary) {
        interceptNetworkErrors(error);
      }
      throw error; // Re-throw the error after handling
    }
  };
}

/**
 * Create api clients for the current app
 * @param {boolean} isLibrary - Whether this is embedded Studio rather than the standalone app
 */
export function createApiClients(isLibrary) {
  const {
    titanDomain, deDomain, contentDomain, appDomain,
  } = getDomains(window.location.hostname);

  if (!titanDomain || !deDomain || !contentDomain) throw (new Error('Missing valid api domains'));

  // Set up Titan Api
  let titanApiFetchClient = createTitanApiFetchClient(titanDomain, isLibrary);

  // Set up DE Api
  let deApiFetchClient = createApiFetchClient(deDomain, isLibrary);

  // Set up App Api
  let appApiClient = createApiFetchClient(
    appDomain.replace('/learn', ''),
    isLibrary,
  );

  NProgress.configure({
    easing: 'easeInQuint',
    trickleSpeed: 800,
  });

  /**
   * Passes all args to the apiFetchClient, but with the Studio api base url.
   * The leading `/studio/v1` is included in the base path and should not be passed.
   * The progress bar is toggled off for embedded Studio.
   */
  let apiProgressBarFetchClient = (...args) => {
    if (!isLibrary) {
      NProgress.start();
    }
    return apiFetchClient(titanDomain, ...args)
      .then((res) => {
        if (!isLibrary) {
          NProgress.done();
        }
        return res;
      }).catch((err) => {
        if (!isLibrary) {
          interceptNetworkErrors(err);
          NProgress.done();
        }
        return err;
      });
  };

  /**
   * Passes all args to the apiFetchClient, but with the Content base url.
   * The path should start with `/api/v1/`.
   */
  const contentApiFetchClient = (...args) => apiFetchClient(contentDomain, ...args);

  // attach 401 interceptors
  deApiFetchClient = attachErrorResponseInterceptor(deApiFetchClient, deApiFetchClient);
  appApiClient = attachErrorResponseInterceptor(appApiClient, deApiFetchClient);
  titanApiFetchClient = attachErrorResponseInterceptor(titanApiFetchClient, deApiFetchClient);

  /*
    Add interceptors to use local data for api requests when present instead
    of requests going to the server. Example use case is for generating thumbnails
    on the server via selenium where draft data is injected into the browser window
    at window.studioAppData. Data in this object will be used instead of data from
    api request.
  */
  titanApiFetchClient = addLocalDataInterceptors(titanApiFetchClient);
  apiProgressBarFetchClient = addLocalDataInterceptors(apiProgressBarFetchClient);
  deApiFetchClient = addLocalDataInterceptors(deApiFetchClient);

  return {
    appApiClient,
    titanApiFetchClient,
    apiProgressBarFetchClient,
    deApiFetchClient,
    contentApiFetchClient,
  };
}

/**
 * Rate limit concurrent network transactions for the content api. See STUD-2699
 *
 * @param {obj} client the fetch client
 * @param {int} limit number of concurrent connections to limit to (set aggressively to 2)
 * @param {int} intervalMs wait time for interval check
 * @returns {function} the rate limited fetch client
 */
export function addRateLimiting(client, limit = 2, intervalMs = 20) {
  let pendingRequests = 0;

  return async function rateLimitedClient(...args) {
    return new Promise((resolve, reject) => {
      // set an interval timer
      const interval = setInterval(() => {
        // if there are too many requests, wait to resolve the request
        if (pendingRequests < limit) {
          pendingRequests += 1;
          clearInterval(interval);

          // wait till the response is resolved to get rid of the pending request
          client(...args)
            .then((res) => {
              pendingRequests = Math.max(0, pendingRequests - 1);
              resolve(res);
            })
            .catch((err) => {
              pendingRequests = Math.max(0, pendingRequests - 1);
              reject(err);
            });
        }
      }, intervalMs);
    });
  };
}
